/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      // AMP3: options can sit in several columns/groups, so move by index
      // through every visible option rather than by DOM siblings.
      const allResults = [...this.results.querySelectorAll('.predictive-search__item')]
        .filter((item) => item.offsetParent !== null);
      if (!allResults.length) return;
      const selectedResult = allResults.find((item) => item.getAttribute('aria-selected') === 'true');
      const current = allResults.indexOf(selectedResult);
      let next = 0;

      if (key === 'ArrowUp') {
        if (!selectedResult) return;
        next = current > 0 ? current - 1 : allResults.length - 1;
      } else if (selectedResult) {
        next = current < allResults.length - 1 ? current + 1 : 0;
      }

      const resultToSelect = allResults[next];
      this.statusEl.textContent = '';
      if (resultToSelect === selectedResult) return;

      resultToSelect.setAttribute('aria-selected', 'true');
      if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');
      resultToSelect.scrollIntoView({ block: 'nearest' });

      this.setLiveRegionText(resultToSelect.textContent.replace(/\s+/g, ' ').trim());
      this.input.setAttribute('aria-activedescendant', resultToSelect.id);
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      // AMP3: four product rows plus up to three extra matches need Shopify's maximum of 10.
      const limit = 10;
      // AMP3: pages, articles and collections too, listed under "Pages" in the side panel (owner, 4 Oct 2026).
      searchParams += `&${encodeURIComponent('resources[type]')}=product,page,article,collection`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=${limit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      this.latestQueryKey = queryKey;
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        // A slower, older response must not replace the latest results.
        if (queryKey !== this.latestQueryKey) return;
        this.renderResults(resultsMarkup);
      } catch (error) {
        // The form still submits to the full search page as normal.
        if (queryKey === this.latestQueryKey) this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.input.setAttribute('aria-activedescendant', '');
      this.setAttribute('results', true);
      this.open();
      this.loadSimilar();
    }

    /**
     * AMP3: fills "You might also like" in the side panel with Shopify's related
     * products for the top match (sections/amp3-sugg-similar), when the query
     * has fewer than three extra matches. Skips products already shown; open-box
     * listings of the top match's product type go first.
     */
    async loadSimilar() {
      const group = this.results.querySelector('[data-a3-similar]');
      if (!group) return;
      const url = group.dataset.a3Similar;
      this.similarCache = this.similarCache || {};

      if (this.similarCache[url] === undefined) {
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(response.status);
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();
          this.similarCache[url] = [...tmpl.content.querySelectorAll('li[data-product-id]')];
        } catch (error) {
          this.similarCache[url] = [];
        }
      }

      // The panel may have been replaced by a newer query while fetching.
      if (!group.isConnected) return;
      const shown = group.dataset.a3SimilarShown;
      const type = group.dataset.a3SimilarType;
      const fresh = this.similarCache[url].filter((li) => !shown.includes(`,${li.dataset.productId},`));
      // Open-box listings of the same product type first; otherwise Shopify's order.
      const promoted = (li) => type && li.hasAttribute('data-openbox') && li.dataset.type === type;
      const items = [...fresh.filter(promoted), ...fresh.filter((li) => !promoted(li))]
        .slice(0, Number(group.dataset.a3SimilarNeed) || 3);
      if (!items.length) return;

      const list = group.querySelector('ul');
      items.forEach((li, i) => {
        const item = li.cloneNode(true);
        item.id = `${this.input.id}-similar-${i + 1}`;
        list.appendChild(item);
      });
      group.hidden = false;
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.positionPanel();
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * AMP3: measures the space available so the wide suggestions panel stays
     * inside the viewport (left offset, room to the right, height below).
     */
    positionPanel() {
      const rect = this.getBoundingClientRect();
      const viewport = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      this.style.setProperty('--a3-sugg-offset', `${-rect.left}px`);
      this.style.setProperty('--a3-sugg-room', `${Math.max(280, document.documentElement.clientWidth - rect.left - 16)}px`);
      this.style.setProperty('--a3-sugg-maxh', `${Math.max(260, viewport - rect.bottom - 16)}px`);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
